import { useState } from 'react';
import { Form, Formik } from 'formik';
import { isEmpty } from 'lodash';
import { parseNearAmount } from '@near-js/utils';

import CloseIcon from 'components/icons/CloseIcon';
import EnterAmountView from './components/views/enter-amount-view';
import SuccessView from './components/views/success-view';

import { StyledContainer, StyledSection } from '../views.style';
import { StyledCloseButton } from './withdraw-storage-view.style';

import { useAppDispatch, useAppSelector } from 'redux/use-hooks';
import { selectActiveToken } from 'redux/slices/tokens';
import { actionAccountSetStorageBalance, selectAccountId } from 'redux/slices/account';
import { refreshAccount } from 'redux/actions/account';
import { actions as statusSliceActions } from 'redux/slices/status';
import { VIEWS_STORAGE_WITHDRAW } from 'lib/constants';
import { validatioSchemaStorage } from '../send-view/form-modal/validation-schema';
import { fungibleTokensService } from 'services/fungible-tokens';
import { getStep } from 'utils';
import { getWalletActionErrorPresentation } from 'utils/wallet';
import { FormValues } from 'types';
import config from 'config';

const { clearLocalAlert, showCustomAlert } = statusSliceActions;
const { OCC_CONTRACT } = config;
const { ENTER_AMOUNT, SUCCESS } = VIEWS_STORAGE_WITHDRAW;

const WithdrawStorageView = ({ closeModal } : { closeModal: () => void }) => {
  const [sendingToken, setSendingToken] = useState(false);
  const [activeStep, setActiveStep] = useState(ENTER_AMOUNT);
  const currentValidationSchema = validatioSchemaStorage[activeStep];
  const isLastStep: boolean = activeStep === ENTER_AMOUNT;

  const dispatch = useAppDispatch();
  const accountId = useAppSelector(selectAccountId);
  const nearTokenBalance = useAppSelector(selectActiveToken);

  const initialValues = { amount: '' };

  const goForward = () => setActiveStep((step) => getStep(step, +1, VIEWS_STORAGE_WITHDRAW));

  const submitHandler = async (
    values: FormValues,
    { touched, validateForm, setTouched, setSubmitting }: any
  ) => {
    const errs = await validateForm();

    if (!isEmpty(errs)) {
      setTouched({ ...touched, ...errs });
      return;
    }

    if (isLastStep) {
      setSendingToken(true);
      try {
        await fungibleTokensService.withdrawStorageDeposit({
          accountId,
          contractName: OCC_CONTRACT,
          amount: parseNearAmount(values.amount),
        });
        setSubmitting(false);
        goForward();
        dispatch(clearLocalAlert());
        dispatch(refreshAccount());
        dispatch(actionAccountSetStorageBalance({ contractName: OCC_CONTRACT, accountId }));
      } catch (e: any) {
        const presentation = getWalletActionErrorPresentation(e);
        dispatch(
          showCustomAlert({
            showAlert: true,
            success: false,
            messageCodeHeader: 'error',
            messageCode: presentation.messageCode,
            errorMessage: presentation.errorMessage
          })
        );
      }
      setSendingToken(false);
      return;
    }

    setSendingToken(false);
    goForward();
  };

  return (
    <StyledContainer>
      <StyledCloseButton onClick={closeModal}>
        <CloseIcon color="#222" />
      </StyledCloseButton>
      <StyledSection p="48px">
        <Formik
          validateOnBlur={false}
          initialValues={initialValues}
          validationSchema={currentValidationSchema(nearTokenBalance)}
          onSubmit={submitHandler}
        >
          {() => {
            return (
              <Form>
                {activeStep === ENTER_AMOUNT && <EnterAmountView isLoading={sendingToken} />}
                {activeStep === SUCCESS && <SuccessView closeModal={closeModal} />}
              </Form>
            )
          }}
        </Formik>
      </StyledSection>
    </StyledContainer>
  )
};

export default WithdrawStorageView;
